/**
 * GlassPill — small inline status badge on glass material.
 *
 * Used for chips, tags, status indicators (e.g. 'Active', '+5 USDT',
 * 'Line 4 closed'). Variants control the accent colour applied to
 * the leading dot and the text glow.
 */
import { type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type Tone = 'neutral' | 'positive' | 'negative' | 'lunar' | 'plasma' | 'nebula';

interface GlassPillProps extends HTMLAttributes<HTMLDivElement> {
  tone?: Tone;
  /** Show a leading colored dot */
  dot?: boolean;
  /** Tighter padding for very compact usage */
  compact?: boolean;
}

const toneText: Record<Tone, string> = {
  neutral: 'text-[var(--text-secondary)]',
  positive: 'text-[var(--status-positive)]',
  negative: 'text-[var(--status-negative)]',
  lunar: 'text-[var(--accent-primary)]',
  plasma: 'text-[var(--accent-secondary)]',
  nebula: 'text-[var(--accent-tertiary)]',
};

const toneDot: Record<Tone, string> = {
  neutral: 'bg-[var(--text-muted)]',
  positive: 'bg-[var(--status-positive)] shadow-[0_0_8px_var(--status-positive)]',
  negative: 'bg-[var(--status-negative)] shadow-[0_0_8px_var(--status-negative)]',
  lunar: 'bg-[var(--accent-primary)] shadow-[0_0_8px_var(--accent-primary)]',
  plasma: 'bg-[var(--accent-secondary)] shadow-[0_0_8px_var(--accent-secondary)]',
  nebula: 'bg-[var(--accent-tertiary)] shadow-[0_0_8px_var(--accent-tertiary)]',
};

export function GlassPill({
  tone = 'neutral',
  dot = false,
  compact = false,
  className,
  children,
  ...rest
}: GlassPillProps) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 lg-pill',
        compact ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1 text-[12px]',
        'font-medium tracking-[0.02em]',
        toneText[tone],
        className,
      )}
      {...rest}
    >
      {dot && (
        <span
          className={cn(
            'inline-block h-1.5 w-1.5 rounded-full',
            toneDot[tone],
          )}
        />
      )}
      {children}
    </div>
  );
}
